import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Save, Loader2, Target, Trophy, Shield, TrendingUp, Calendar } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const NumRow = ({ label, value, onChange, suffix = "%", step = "0.1", testId }) => (
  <div>
    <label className="vx-label mb-1 block">{label}</label>
    <div className="relative">
      <Input
        type="number" step={step} value={value}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        data-testid={testId}
        className="bg-[#111827] border-[#1f2937] font-mono text-xs pr-10"
      />
      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-mono text-[#6b7280]">{suffix}</span>
    </div>
  </div>
);

export default function StartChallengeDialog({ open, onClose, accountId, onCreated }) {
  const [presets, setPresets] = useState([]);
  const [presetId, setPresetId] = useState("ftmo_100k_phase1");
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await api.get("/prop/presets");
        setPresets(r.data?.presets || []);
      } catch { /* silent */ }
    })();
  }, [open]);

  useEffect(() => {
    const p = presets.find((x) => x.preset_id === presetId);
    if (p) {
      setForm({
        starting_balance: p.starting_balance,
        profit_target_pct: p.profit_target_pct,
        max_daily_loss_pct: p.max_daily_loss_pct,
        max_total_drawdown_pct: p.max_total_drawdown_pct,
        min_trading_days: p.min_trading_days,
        max_trading_days: p.max_trading_days,
      });
    }
  }, [presetId, presets]);

  const isCustom = presetId === "custom";

  const submit = async () => {
    setSaving(true);
    try {
      const body = isCustom ? { ...form, preset_id: null, firm_name: "Custom", challenge_type: "custom" } : { preset_id: presetId, ...form };
      await api.post(`/accounts/${accountId}/challenge`, body);
      toast.success("Challenge started · risk limits synced");
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start challenge");
    } finally { setSaving(false); }
  };

  const selected = useMemo(() => presets.find((p) => p.preset_id === presetId), [presets, presetId]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-[#0a0e1a] border-[#1f2937] text-[#e5e7eb]" data-testid="start-challenge-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl tracking-tight flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#f59e0b]" /> Start Prop Firm Challenge
          </DialogTitle>
          <DialogDescription className="text-xs text-[#9ca3af]">
            Pick a firm preset and starting balance to begin tracking your challenge.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
          <div>
            <label className="vx-label mb-1 block">Preset</label>
            <Select value={presetId} onValueChange={setPresetId}>
              <SelectTrigger className="bg-[#111827] border-[#1f2937] text-sm" data-testid="preset-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0e1a] border-[#1f2937] text-[#e5e7eb] max-h-[280px]">
                {presets.map((p) => (
                  <SelectItem key={p.preset_id} value={p.preset_id}>
                    {p.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selected && (
              <div className="mt-2 text-[11px] font-mono text-[#6b7280]">
                {selected.firm_name} · {selected.challenge_type} · ${selected.starting_balance.toLocaleString()}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <NumRow
              label="Starting balance" value={form.starting_balance || 0}
              onChange={(v) => setForm({ ...form, starting_balance: v })}
              suffix="$" step="1000"
              testId="ch-starting-balance"
            />
            <NumRow
              label="Profit target" value={form.profit_target_pct ?? 0}
              onChange={(v) => setForm({ ...form, profit_target_pct: v })}
              testId="ch-profit-target"
            />
            <NumRow
              label="Max daily loss" value={form.max_daily_loss_pct ?? 0}
              onChange={(v) => setForm({ ...form, max_daily_loss_pct: v })}
              testId="ch-max-daily"
            />
            <NumRow
              label="Max total drawdown" value={form.max_total_drawdown_pct ?? 0}
              onChange={(v) => setForm({ ...form, max_total_drawdown_pct: v })}
              testId="ch-max-dd"
            />
            <NumRow
              label="Min trading days" value={form.min_trading_days ?? 0}
              onChange={(v) => setForm({ ...form, min_trading_days: v })}
              suffix="days" step="1"
              testId="ch-min-days"
            />
            <NumRow
              label="Max trading days" value={form.max_trading_days ?? ""}
              onChange={(v) => setForm({ ...form, max_trading_days: v })}
              suffix="days" step="1"
              testId="ch-max-days"
            />
          </div>

          <div className="p-3 rounded border border-[#3b82f6]/40 bg-[#3b82f6]/10 flex items-start gap-2">
            <Shield className="w-4 h-4 text-[#3b82f6] shrink-0 mt-0.5" />
            <div className="text-[11px] text-[#9ca3af] leading-relaxed">
              <b className="text-[#3b82f6]">Concierge</b>: Starting this challenge will override this account&apos;s
              risk settings — daily loss cap, max drawdown, and equity kill-switch will auto-match the firm&apos;s limits.
              Your existing signals & journal are preserved.
            </div>
          </div>
        </div>
        <DialogFooter>
          <button
            onClick={onClose}
            className="h-9 px-4 rounded border border-[#1f2937] hover:bg-[#1f2937] text-sm text-[#9ca3af] flex items-center gap-1"
          >
            <X className="w-3.5 h-3.5" /> Cancel
          </button>
          <button
            onClick={submit} disabled={saving}
            data-testid="start-challenge-btn"
            className="h-9 px-5 rounded bg-[#f59e0b] hover:bg-[#d97706] text-white text-sm flex items-center gap-1 disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trophy className="w-3.5 h-3.5" />}
            {saving ? "Starting…" : "Start Challenge"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
